import { BiDashboardGhost } from '@euler/app/components/message/widgets/bi-dashboard-changes/BiDashboardHostContext';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDashboardView, BiLayoutItem } from '@euler/model/bi/dashboard';
import { useCreation } from '@euler/utils/hooks';
import { memo } from 'react';
import { View } from 'react-native';
// The gesture-handler scroll view, so a card's resize grabber can take a
// drag from it on a touch screen.
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BiButton } from '../components/ui/BiButton';
import { kBiContentMaxWidth } from '../components/ui/BiPageHeader';
import { DashboardEmpty } from './DashboardEmpty';
import { GhostDashboard } from './ghost/GhostDashboard';
import { BiRenderCard, DashboardGrid } from './grid/DashboardGrid';
import { BiViewportProvider, BiViewportTicks } from './viewport/BiViewport';

/**
 * The cards of a loaded dashboard: its grid, the preview of a pending AI
 * change set in its place, or the empty state.
 */
export const DashboardContent = memo(
  ({
    dashboard,
    ghost,
    chatBusy,
    renderCard,
    renderPlainCard,
    onArrange,
    onResizeNarrow,
    onStackedChange,
    onAdd,
    onApplyGhost,
    onDiscardGhost,
  }: {
    dashboard: BiDashboardView;
    /** Drawn instead of the grid while it applies to this revision. */
    ghost: BiDashboardGhost | null;
    chatBusy: boolean;
    renderCard: BiRenderCard;
    /** A card without its actions, for the preview. */
    renderPlainCard: BiRenderCard;
    onArrange?: (items: BiLayoutItem[]) => Promise<boolean>;
    onResizeNarrow?: (cardId: string, rows: number) => Promise<boolean>;
    /** Whether the grid draws the phone view (one card per row). */
    onStackedChange: (stacked: boolean) => void;
    /** Given to editors. */
    onAdd: (() => void) | null;
    onApplyGhost: (changeSetId: string) => void;
    onDiscardGhost: (changeSetId: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const ticks = useCreation(() => new BiViewportTicks(), []);
    return (
      <BiViewportProvider ticks={ticks}>
        <ScrollView
          // Cards read their data as they come into view.
          onScroll={ticks.notify}
          scrollEventThrottle={100}
          onLayout={ticks.notify}
          onContentSizeChange={ticks.notify}
          contentContainerStyle={{
            padding: rpx(16),
            paddingBottom: insets.bottom + rpx(72),
          }}
        >
          <View
            style={{
              width: '100%',
              maxWidth: kBiContentMaxWidth,
              alignSelf: 'center',
            }}
          >
            {ghost ? (
              <GhostDashboard
                dashboard={dashboard}
                ghost={ghost}
                busy={chatBusy}
                renderCard={renderPlainCard}
                onApply={() => onApplyGhost(ghost.changeSetId)}
                onDiscard={() => onDiscardGhost(ghost.changeSetId)}
              />
            ) : dashboard.cards.length ? (
              <DashboardGrid
                layout={dashboard.layout}
                revision={dashboard.revision}
                cards={dashboard.cards}
                renderCard={renderCard}
                onArrange={onArrange}
                onResizeNarrow={onResizeNarrow}
                onStackedChange={onStackedChange}
              />
            ) : (
              <DashboardEmpty
                action={
                  onAdd ? (
                    <BiButton
                      title="手动添加图表"
                      tone="primary"
                      onPress={onAdd}
                    />
                  ) : null
                }
              />
            )}
          </View>
        </ScrollView>
      </BiViewportProvider>
    );
  },
);
